import { useDispatch } from "react-redux";
import {
    Autocomplete,
    Grid,
    IconButton,
    ListItem,
    TextField,
    Button,
} from "@mui/material";

import { MinusCircle, AddCircle } from "@solar-icons/react/ssr";
import { useEffect, useState } from "react";
import { serviceItems } from "@/sheet/serviceItemsStore";
import { request } from "@/Api";
import {
    getOfficeCurrency,
    getLineItemRate,
    getReadOnly,
    getIsProjectPepsi,
    getShowRunning,
} from "@/sheet/bidStore";
import { countryCurrencies } from "@/constants";
import {
    BID_CATEGORY_PRODUCTION_SERVICES,
    BID_SECTION_PRODUCTION_SERVICES,
    BID_SECTION_TALENT,
    BID_SECTION_TRAVEL,
} from "@/sheet/constants";
import { setUpdating } from "@/sheet/bidSlice";

// TODO: make this component a Cell Template otherwise:
// component re-instantiates and re-mounts (so not just re-render)
// not consistant with other custom cells

export const AddButtonCell = ({ type, parent, serviceItemOptions }) => {
    const dispatch = useDispatch();

    const [adding, setAdding] = useState();
    const [inputValue, setInputValue] = useState("");
    const [highlightedValue, setHighlightedValue] = useState("");

    const readOnly = getReadOnly();
    const isRunning = getShowRunning();

    const isProjectPepsi = getIsProjectPepsi();

    const handleInputKeyDown = (e) => {
        e.stopPropagation();
    };
    const handleAddButtonClick = (e) => {
        setAdding(true);
    };
    const handleCancelButtonClick = (e) => {
        setAdding(false);
        setInputValue("");
        setHighlightedValue("");
    };

    const getSectionDefaultMarkup = (sectionName) => {
        if ([BID_SECTION_TALENT, BID_SECTION_TRAVEL].includes(sectionName)) {
            if (sectionName == BID_SECTION_TRAVEL && isProjectPepsi) {
                return 0.1;
            }
            return 0.15;
        } else if (sectionName == BID_SECTION_PRODUCTION_SERVICES) {
            return 0;
        } else {
            return 0.3;
        }
    };

    const addLine = (value) => {
        const officeCurrency = getOfficeCurrency();
        const baseData = serviceItems.find(
            (item) =>
                item.name == value &&
                (item.category == parent.name ||
                    (parent.is_internal &&
                        item.category == BID_CATEGORY_PRODUCTION_SERVICES)),
        ) || { name: value };
        const lineItemData = {
            ...baseData,
            bid_id: parent.bid_id,
            line_group_id: parent.id,
            service_item_id: baseData.id || null,
            is_internal: parent.is_internal,
            vendor_currency: baseData.country_code
                ? countryCurrencies[baseData.country_code]
                : officeCurrency || "CAD",
            vendor_rate: getLineItemRate(0, baseData, parent.rate_type),
            markup: parent.markup || 0,
            markup_amount: parent.markup_amount || 0,
            budget_buffer: parent.budget_buffer || 0,
            budget_buffer_amount: parent.budget_buffer_amount || 0,
            rate_type: parent.rate_type,
            sort_order: parent.line_items.length
                ? Math.max(...parent.line_items.map((c) => c.sort_order)) + 1
                : 1,
            days: 1,
            quantity: 0,
        };
        request.post(
            `/bids/${parent.bid_id}/line-items${isRunning ? "/running" : ""}`,
            lineItemData,
        );
    };

    const addCategory = (value) => {
        request.post(
            `/bids/${parent.bid_id}/line-groups${isRunning ? "/running" : ""}`,
            {
                name: value,
                is_internal: parent.is_internal,
                rate_type: parent.rate_type,
                vendor_currency: parent.vendor_currency || "CAD",
                markup: parent.markup || 0,
                markup_amount: parent.markup_amount || 0,
                budget_buffer: parent.budget_buffer || 0,
                budget_buffer_amount: parent.budget_buffer_amount || 0,
                sort_order: parent.children.length
                    ? Math.max(...parent.children.map((c) => c.sort_order)) + 1
                    : 1,
                bid_id: parent.bid_id,
                parent_id: parent.id,
            },
        );
    };

    const addSection = (value) => {
        const rootGroups = isRunning
            ? parent.line_items_grouped_running
            : parent.line_items_grouped;
        const officeCurrency = getOfficeCurrency();
        request.post(
            `/bids/${parent.id}/line-groups${isRunning ? "/running" : ""}`,
            {
                name: value,
                markup: getSectionDefaultMarkup(value),
                markup_amount: 0,
                budget_buffer: 0,
                budget_buffer_amount: 0,
                is_internal: false,
                bid_id: parent.id,
                sort_order: rootGroups.length
                    ? Math.max(...rootGroups.map((s) => s.sort_order)) + 1
                    : 1,
                rate_type: "Days",
                lineGroupType: "section",
                popupType: "create",
                vendor_currency: officeCurrency || "CAD",
            },
        );
    };

    const addItem = (value) => {
        dispatch(setUpdating(true));
        setAdding(false);
        if (type == "line") {
            addLine(value, isRunning);
        } else if (type == "category") {
            addCategory(value);
        } else if (type == "section") {
            addSection(value);
        }
    };

    useEffect(() => {
        setAdding(false);
    }, []);

    return (
        <Grid container alignItems={"center"} wrap="nowrap">
            <Grid item>
                {adding ? (
                    <IconButton size="small" onClick={handleCancelButtonClick}>
                        <MinusCircle size={14} />
                    </IconButton>
                ) : (
                    <Button
                        disabled={readOnly && !isRunning}
                        startIcon={
                            <AddCircle
                                color={
                                    readOnly && !isRunning
                                        ? "secondary"
                                        : "success"
                                }
                            />
                        }
                        onClick={handleAddButtonClick}
                    >
                        <span style={{ fontSize: 12 }}>
                            Add {`${isRunning ? "running" : ""} ${type}`}
                        </span>
                    </Button>
                )}
            </Grid>
            <Grid item width={180}>
                {adding && (
                    <Autocomplete
                        style={{ padding: 0 }}
                        value={inputValue}
                        disableClearable
                        freeSolo
                        size="small"
                        renderInput={(params) => (
                            <TextField
                                placeholder={`Add ${type}`}
                                autoFocus
                                {...params}
                                onPointerDown={(e) => {
                                    e.stopPropagation();
                                }}
                                inputProps={{
                                    ...params.inputProps,
                                    style: {
                                        fontSize: 12,
                                        border: "none",
                                        padding: 2,
                                        paddingLeft: 6,
                                    },
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    style: { padding: 0, fontSize: 12 },
                                }}
                            />
                        )}
                        onKeyDown={handleInputKeyDown}
                        options={serviceItemOptions}
                        onPointerDown={(e) => {
                            e.stopPropagation();
                        }}
                        onChange={(e, v) => {
                            setInputValue(v);
                        }}
                        onHighlightChange={(e, v) => {
                            setHighlightedValue(v);
                        }}
                        onKeyPress={(e) => {
                            e.key != "Enter" ||
                                addItem(highlightedValue || e.target.value);
                        }}
                        renderOption={(props, option, state) => (
                            <ListItem
                                {...props}
                                key={option}
                                style={{
                                    padding: 5,
                                    paddingLeft: 10,
                                    fontSize: 12,
                                }}
                                onPointerDown={(event) =>
                                    event.stopPropagation()
                                } // Custom pointer down handler for each option
                            >
                                {option}
                            </ListItem>
                        )}
                    />
                )}
            </Grid>
        </Grid>
    );
};
